import React, { useMemo } from 'react';
import { fmt, dayLabel, money } from './format';
import { buildTodayOutlook, buildLeftToSpend } from './dataAdapters';
import { CashLine } from './charts';

// The page answers one question — "can I spend right now?" — with one figure,
// then shows the two lists that make it that number. Everything here is
// anchored to today, which is why there is no month picker and no "Live" tag:
// there is nothing on the page a month could scope.
//
// Colour policy: neutral by default, one alarm colour, used only where the
// user has something to act on. Nothing is coloured green for being fine.

const EYEBROW = 'today-eyebrow';

// U+2212, not a hyphen — a hyphen at this weight reads as a dash between
// words rather than a minus in front of a figure.
const MINUS = '−';

function Eyebrow({ children }) {
  return <div className={EYEBROW}>{children}</div>;
}

function Row({ children, onClick, ariaLabel }) {
  if (!onClick) return <div className="today-row">{children}</div>;
  return (
    <button type="button" className="today-row" onClick={onClick} aria-label={ariaLabel}>
      {children}
    </button>
  );
}

// The lead figure is the balance: it is the one number on this page the bank
// reported rather than one we derived, and it is what the user came to check.
// Safe-to-spend sits directly under it as the second figure — still the
// judgement the page exists to make, but visibly downstream of the fact it is
// computed from, and now legible as a subtraction rather than a mystery.
function Hero({ outlook, compact, hasCards, balanceAgeLabel }) {
  const short = outlook.safeToSpend < 0;
  const overdrawn = outlook.balance < 0;
  // The one alarm colour, and only on the figure that is actually wrong: a
  // negative balance, or a shortfall against the bills already committed.
  const strong = (on) => ({ fontWeight: 600, color: on ? 'var(--alarm)' : 'var(--ink)' });
  const horizon = outlook.deposit ? (
    <>
      your next deposit on <b style={strong(short)}>{dayLabel(outlook.deposit.date)}</b>
    </>
  ) : (
    // No income on the horizon, so "before your next deposit" would be a claim
    // we can't support. Say what we actually measured instead.
    <>
      the end of the month on <b style={strong(short)}>{dayLabel(outlook.horizonDate)}</b>{' '}
      — no upcoming deposit found
    </>
  );

  return (
    <div className="rise">
      <Eyebrow>{hasCards ? 'Cash balance' : 'Balance'}</Eyebrow>
      <div
        className="today-figure"
        style={{ color: overdrawn ? 'var(--alarm)' : 'var(--ink)', fontSize: compact ? 64 : undefined }}
      >
        {money(outlook.balance, { cents: false })}
      </div>
      {/* The figure is only as current as the balance it starts from, so a
          stale balance has to say so here rather than quietly skew it. */}
      {balanceAgeLabel && (
        <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--alarm)', fontWeight: 500 }}>
          {balanceAgeLabel} — may be out of date
        </div>
      )}
      <div className="today-derived">
        <span className="num today-figure-2" style={{ color: short ? 'var(--alarm)' : 'var(--ink)' }}>
          {/* Unsigned when short: the sentence already says "short of", and
              "-$120 short" reads as a double negative. */}
          {money(short ? Math.abs(outlook.safeToSpend) : outlook.safeToSpend, { cents: false })}
        </span>
        <span style={{ fontSize: 14, lineHeight: 1.5, color: short ? 'var(--alarm)' : 'var(--ink-2)' }}>
          {short ? 'short of the ' : 'safe to spend after the '}
          <b style={strong(short)}>{money(outlook.billsTotal, { cents: false })}</b>
          {' '}of bills due before {horizon}
        </span>
      </div>
    </div>
  );
}

function OutBeforePayday({ outlook, onOpenSeries }) {
  return (
    <div className="rise" style={{ display: 'flex', flexDirection: 'column', gap: 4, animationDelay: '80ms' }}>
      <div style={{ marginBottom: 10 }}>
        <Eyebrow>{outlook.deposit ? 'Out before payday' : 'Out before month end'}</Eyebrow>
      </div>
      {outlook.rows.length === 0 ? (
        <p style={{ fontSize: 13.5, color: 'var(--ink-2)' }}>
          Nothing scheduled between now and {dayLabel(outlook.horizonDate)}.
        </p>
      ) : outlook.rows.map((r) => (
        <Row
          key={r.id}
          onClick={r.income ? undefined : () => onOpenSeries(r)}
          ariaLabel={`${r.label}, ${fmt(r.amount, { cents: false })} on ${dayLabel(r.date)}`}
        >
          <span className="num" style={{ width: 52, flexShrink: 0, fontSize: 12, color: 'var(--ink-3)' }}>
            {dayLabel(r.date)}
          </span>
          <span style={{ flex: 1, fontSize: 14.5, fontWeight: 500, color: r.income ? 'var(--accent)' : 'var(--ink)' }}>
            {r.label}
            {r.subLabel && <span style={{ color: 'var(--ink-3)', fontWeight: 400 }}>{r.subLabel}</span>}
            {/* Entered ahead of its first charge — counted, but not yet seen. */}
            {r.planned && <span style={{ color: 'var(--ink-3)', fontWeight: 400 }}> · planned</span>}
            {/* Due already but not posted — still owed, so still counted. */}
            {r.missed && <span style={{ color: 'var(--ink-3)', fontWeight: 400 }}> · not seen yet</span>}
          </span>
          <span className="num" style={{ fontSize: 14.5, fontWeight: 600, color: r.income ? 'var(--accent)' : 'var(--ink)' }}>
            {money(r.amount, { cents: false, sign: r.income })}
          </span>
        </Row>
      ))}
    </div>
  );
}

function LeftToSpend({ rows, onOpenCategory }) {
  const budgeted = rows.reduce((s, r) => s + r.limit, 0);
  const overCount = rows.filter((r) => r.over).length;

  return (
    <div className="rise" style={{ display: 'flex', flexDirection: 'column', gap: 4, animationDelay: '120ms' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
        <Eyebrow>Left to spend</Eyebrow>
        {budgeted > 0 && (
          <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>of {money(budgeted, { cents: false })} budgeted</span>
        )}
      </div>
      {rows.length === 0 ? (
        <p style={{ fontSize: 13.5, color: 'var(--ink-2)' }}>No budgets set yet — add limits in Plan.</p>
      ) : (
        <>
          {rows.map((r) => (
            <Row key={r.cat} onClick={() => onOpenCategory(r.cat)} ariaLabel={`${r.cat}: view transactions`}>
              <span style={{ flex: 1, fontSize: 14.5, fontWeight: 500, color: 'var(--ink)' }}>{r.cat}</span>
              {/* Plain ink under the limit; the one alarm colour over it. No
                  bars, and no amber "near limit" — the remaining dollars are
                  the whole signal. */}
              <span className="num" style={{ fontSize: 14.5, fontWeight: 600, color: r.over ? 'var(--alarm)' : 'var(--ink)' }}>
                {r.over
                  ? <>{MINUS}{money(Math.abs(r.remaining), { cents: false })} over</>
                  : <>{money(r.remaining, { cents: false })} left</>}
              </span>
            </Row>
          ))}
          <div style={{ marginTop: 16, fontSize: 12.5, color: 'var(--ink-2)', lineHeight: 1.55 }}>
            {overCount === 0
              ? 'Nothing over budget. A number in plain ink is a number you don’t have to act on.'
              : `${overCount === 1 ? 'One category' : `${overCount} categories`} over. Nothing else is coloured — a number in plain ink is a number you don’t have to act on.`}
          </div>
        </>
      )}
    </div>
  );
}

export function TodayView({
  balance,
  runway,
  cards = [],
  budgetRows = [],
  onOpenSeries,
  onOpenCategory,
  welcome = null,
  compact = false,
  balanceAgeLabel = null,
  banner = null,
}) {
  const outlook = useMemo(
    () => buildTodayOutlook(runway, { balance, cards }),
    [runway, balance, cards],
  );
  const leftToSpend = useMemo(() => buildLeftToSpend(budgetRows), [budgetRows]);

  // 30 days plus today's anchor. The ledger runs to 60 so the runway
  // assessment has room; the chart's question only reaches a month out.
  const chartDays = useMemo(() => (runway?.days || []).slice(0, 31), [runway]);
  const low = useMemo(() => {
    if (chartDays.length === 0) return null;
    return chartDays.reduce((lo, d) => (d.balance < lo.balance ? d : lo), chartDays[0]);
  }, [chartDays]);

  // No bank connected, so there is no ledger to answer the question with:
  // the checklist takes the hero's place and the chart is dropped entirely.
  if (!outlook) {
    return <div className="today">{banner}{welcome}</div>;
  }

  return (
    <div className="today">
      {/* The connection banner is the one thing allowed to interrupt the hero:
          a broken bank feed means the figure below it is built on stale data. */}
      {banner}
      {welcome}
      <Hero
        outlook={outlook}
        compact={compact}
        hasCards={cards.length > 0}
        balanceAgeLabel={balanceAgeLabel}
      />

      {chartDays.length > 1 && (
        <div className="rise" style={{ display: 'flex', flexDirection: 'column', gap: 12, animationDelay: '40ms' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
            <Eyebrow>Cash, next 30 days</Eyebrow>
            {low && (
              <span style={{ fontSize: 12.5, color: 'var(--ink-2)' }}>
                low point{' '}
                <b className="num" style={{ color: low.balance < 0 ? 'var(--alarm)' : 'var(--ink)', fontWeight: 600 }}>
                  {money(low.balance, { cents: false })}
                </b>{' '}
                on {dayLabel(low.date)}
              </span>
            )}
          </div>
          <CashLine days={chartDays} compact={compact} depositDate={outlook.deposit?.date || null} />
        </div>
      )}

      <div className="today-cols">
        <OutBeforePayday outlook={outlook} onOpenSeries={onOpenSeries} />
        <LeftToSpend rows={leftToSpend} onOpenCategory={onOpenCategory} />
      </div>
    </div>
  );
}
